export interface ApiErrorBody {
  code: string;
  message: string;
  requestId?: string;
  details?: unknown;
}

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly body: ApiErrorBody) {
    super(body.message);
  }
}

export function errorMessage(error: unknown) {
  if (!(error instanceof ApiError)) return '操作失败，请稍后重试';
  if (error.body.code === 'VALIDATION_ERROR' && Array.isArray(error.body.details)) {
    const issue = error.body.details.find((detail): detail is { message: string } => (
      typeof detail === 'object' && detail !== null && 'message' in detail
      && typeof (detail as { message?: unknown }).message === 'string'
    ));
    if (issue) return issue.message;
  }
  return error.message;
}

function cookie(name: string) {
  const entry = document.cookie.split('; ').find((part) => part.startsWith(`${name}=`));
  return entry ? decodeURIComponent(entry.slice(name.length + 1)) : '';
}

async function request<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const method = init.method?.toUpperCase() ?? 'GET';
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('content-type')) headers.set('content-type', 'application/json');
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method) && path.includes('/admin/')) {
    const csrf = cookie('cowrite_csrf');
    if (csrf) headers.set('x-csrf-token', csrf);
  }
  const response = await fetch(`/api/v1${path}`, { ...init, headers, credentials: 'include' });
  if (response.status === 401 && retry && !path.startsWith('/admin/session/')) {
    const refreshed = await fetch('/api/v1/admin/session/refresh', { method: 'POST', credentials: 'include' });
    if (refreshed.ok) return request<T>(path, init, false);
  }
  if (!response.ok) {
    let body: ApiErrorBody = { code: 'HTTP_ERROR', message: `请求失败（${response.status}）` };
    try { body = await response.json() as ApiErrorBody; } catch { /* keep fallback */ }
    throw new ApiError(response.status, body);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, {
    method: 'POST',
    ...(body === undefined ? {} : { body: JSON.stringify(body) })
  })
};
